import { types as t, getParent, flow } from 'mobx-state-tree';

import { config } from 'app-config';
import { UserRole } from 'models/users';
import { withEnv } from 'models/withEnv';

const DepartmentTranslation = t.model('DepartmentTranslation', {
  language: t.string,
  title: t.string,
});

const Department = t.model('Department', {
  id: t.identifierNumber,
  translations: t.array(DepartmentTranslation),
});

export const User = t
  .model('User', {
    id: t.identifierNumber,
    email: '',
    isAdmin: false,
    isActive: false,
    isSystemUser: t.maybeNull(t.optional(t.boolean, false)),
    name: '',
    role: t.optional(UserRole, 'user'),
    uuid: t.maybe(t.string),
    picture: t.maybeNull(t.optional(t.string, '')),
    followingJobs: t.array(t.number),
    followingCandidates: t.array(t.number),
    hasSetPassword: false,
    addToNewJobs: false,
    addToHiredApplications: false,
    status: t.maybeNull(t.string),
    language: t.maybeNull(t.optional(t.string, 'en')),
    departmentIds: t.maybeNull(t.array(t.number)),
    departments: t.maybeNull(t.array(Department)),

    loading: false,
    loaded: false,

    // For editing
    shouldSendInvite: true,
    errors: t.map(t.array(t.string)),
  })
  .views(withEnv)
  .views((self) => ({
    get profileUrl() {
      return `/settings/team/${self.id}/profile`;
    },
    get jobFollowCount() {
      return self.followingJobs.length;
    },
    get candidateFollowCount() {
      return self.followingCandidates.length;
    },
    get displayName() {
      return self.isActive ? self.name : `${self.name} (${self.env.t('user.inactive')})`;
    },
    get companyInfo() {
      return self.env.current.info;
    },
    get roleDisplay() {
      return self.env.t(`user.role.${self.role}`);
    },
    get departmentsDisplay() {
      if (self.departments) {
        const defaultLanguage = self.companyInfo.defaultLanguage;

        return self.departments
          .map(({ translations }) => {
            const defaultLang = translations.find(({ language }) => language === defaultLanguage);
            return defaultLang?.title ?? translations[0]?.title;
          })
          .join(', ');
      }
      return null;
    },
  }))
  .actions((self) => ({
    setErrors(errors) {
      self.errors = errors;
    },
    sendInvite: flow(function* () {
      const res = yield self.env.api.users.sendInvite(self.id);
      if (res.error) {
        self.env.showError(self.env.t('user.sendinvite.error'));
        return false;
      } else {
        self.env.showSuccess(self.env.t('user.sendinvite.success'));
        return true;
      }
    }),
    toggleShouldSendInvite() {
      self.shouldSendInvite = !self.shouldSendInvite;
    },
    toggleAddToNewJobs() {
      self.addToNewJobs = !self.addToNewJobs;
    },
    setAddToNewJobs(value) {
      self.addToNewJobs = value;
    },
    toggleAddToHiredApplications() {
      self.addToHiredApplications = !self.addToHiredApplications;
    },
    setAddToHiredApplications(value) {
      self.addToHiredApplications = value;
    },
    create: flow(function* () {
      const res = yield self.env.api.users.create({
        sendInvite: self.shouldSendInvite,
        name: self.name,
        email: self.email,
        role: self.role,
        addToNewJobs: self.addToNewJobs,
        addToHiredApplications: self.addToHiredApplications,
      });

      if (res.error) {
        if (res.data.email?.[0].includes('already exists')) {
          self.env.showError(self.env.t('edituser.create.email.error'), { subtitle: self.email });
        } else {
          self.env.showError(self.env.t('edituser.create.error'));
        }
        self.errors = res.data;
        return false;
      } else {
        const user = self.env.userStore.add(res.data);
        self.env.showSuccess(self.env.t('edituser.create.success'));
        self.followingJobs.map((job) => user.grantJobAccess(job));
        return true;
      }
    }),
    setFollowingJobs(jobs) {
      self.followingJobs = jobs;
    },
    setFollowingCandidates(candidates) {
      self.followingCandidates = candidates;
    },
    setPicture(picture) {
      self.picture = picture;
    },
    jobSharingUrl(id, referral) {
      if (!self.companyInfo || !self.uuid) return '';
      const name = self.companyInfo.name.toLowerCase();
      const prefix = config.CAREERS_URL;
      if (referral) {
        return `${prefix}/${name}/${id}/?referral_uuid=${self.uuid}`;
      }
      return `${prefix}/${name}/${id}`;
    },
    setName(name) {
      self.name = name;
    },
    setRole(role) {
      self.role = role;
      // Clear job and candidate follows when downgrading to employee
      if (role === 'user') {
        self.followingJobs = [];
        self.followingCandidates = [];
      }
    },
    setUuid(uuid) {
      self.uuid = uuid;
    },
    setEmail(email) {
      self.email = email;
    },
    setLanguage(language) {
      self.language = language;
    },
    followJob(job) {
      if (!self.followingJobs.includes(job)) {
        self.followingJobs.push(job);
      }
    },
    unfollowJob(job) {
      self.followingJobs = self.followingJobs.filter((j) => j !== job);
    },
    followCandidate(candidateId) {
      if (!self.followingCandidates.includes(candidateId)) {
        self.followingCandidates.push(candidateId);
      }
    },
    unfollowCandidate(candidateId) {
      self.followingCandidates = self.followingCandidates.filter((id) => id !== candidateId);
    },
    delete() {
      return getParent(self, 2).delete(self);
    },
    load: flow(function* () {
      if (self.loaded || self.loading) return;
      self.loading = true;

      const res = yield self.env.api.users.loadUser(self.id);
      res.data.forEach((d) => {
        self.items.put(d);
        if (!self.items.has(d)) {
          self.list.push(d.id);
        }
      });

      self.loaded = true;
      self.loading = false;
    }),
    revokeJobAccess: flow(function* (jobId) {
      const res = yield self.env.api.jobs.bulkPurgeAccess(self.id, jobId);
      const job = self.env.jobStore.items.get(jobId);
      if (!res.error) {
        self.env.showSuccess(self.env.t('user.revoke.success'));
        job.removeFollower(self.id);
        self.unfollowJob(job.id);
        job.applications.forEach((application) => {
          application.shallowRemoveFollower(self);
          self.unfollowCandidate(application.id);
        });
      } else {
        self.env.showError(self.env.t('user.revoke.error'));
      }
    }),
    grantJobAccess: flow(function* (jobId, notifyOnSuccess = false) {
      const res = yield self.env.api.users.grantJobAccess(self.id, jobId);
      const job = self.env.jobStore.items.get(jobId);

      if (!res.error) {
        if (notifyOnSuccess) {
          self.env.showSuccess(self.env.t('user.grant.success'));
        }
        job.addFollower(self);
        self.followJob(job.id);
        return true;
      } else {
        self.env.showError(self.env.t('user.grant.error'));
        return false;
      }
    }),
  }));
